// ============================================================
// ChordAnalysisLabel
// ============================================================
// Renders harmonic analysis information below a chord symbol.
// Only shown when analysis is enabled and relevant layers
// are turned on in analysisSettings.
//
// Displays (based on settings):
//   - Function label: T, S, D, BD, BT, BS, SD
//   - Secondary dominant: V/V, V/IV etc.
//   - Roman numeral degree: I, ii, V7 etc.
//   - Borrowed indicator: small tint on the bar cell
// ============================================================

import React from 'react';
import type { Chord, Key, AnalysisSettings } from '../../core/types';
import {
  analyseChord,
  FUNCTION_TEXT_COLORS,
  FUNCTION_COLORS,
} from '../../core/analysis';

interface ChordAnalysisLabelProps {
  chord: Chord;
  sectionKey: Key;
  settings: AnalysisSettings;
  /** If true, show a tinted background on the bar cell */
  showBackground?: boolean;
}

export function ChordAnalysisLabel({
  chord,
  sectionKey,
  settings,
  showBackground = false,
}: ChordAnalysisLabelProps) {
  if (!settings.enabled) return null;
  if (!settings.showFunction && !settings.showDegree && !settings.showSecondary) return null;

  const analysis = analyseChord(chord, sectionKey);
  const textColor = FUNCTION_TEXT_COLORS[analysis.function];

  // Determine what label to show
  let displayLabel: string | null = null;

  if (settings.showSecondary && analysis.isSecondary) {
    displayLabel = analysis.label; // e.g. "V/V"
  } else if (settings.showFunction) {
    displayLabel = analysis.function; // e.g. "T", "D", "BD"
  }

  const showDegreeLabel = settings.showDegree;

  if (!displayLabel && !showDegreeLabel) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 3,
        lineHeight: 1,
      }}
    >
      {/* Function / secondary label */}
      {displayLabel && (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.05em',
            color: textColor,
            background: showBackground ? FUNCTION_COLORS[analysis.function] : 'transparent',
            borderRadius: 2,
            padding: showBackground ? '1px 3px' : '0',
            lineHeight: 1.4,
          }}
        >
          {displayLabel}
        </span>
      )}

      {/* Borrowed indicator dot */}
      {settings.showBorrowed && analysis.borrowed && !analysis.isSecondary && (
        <span
          title="Borrowed chord"
          style={{
            width: 4,
            height: 4,
            borderRadius: '50%',
            background: 'var(--analysis-borrowed-text)',
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------
// BAR BACKGROUND TINT
// Returns a background colour for a bar cell based on the
// dominant harmonic function of its chords (optional feature)
// ------------------------------------------------------------

export function getBarAnalysisTint(
  chords: Chord[],
  key: Key,
  settings: AnalysisSettings,
): string | undefined {
  if (!settings.enabled || !settings.showBorrowed) return undefined;
  if (chords.length === 0) return undefined;

  // If any chord is borrowed, tint the bar
  const hasBorrowed = chords.some((c) => {
    const a = analyseChord(c, key);
    return a.borrowed;
  });

  return hasBorrowed ? 'rgba(168, 85, 247, 0.04)' : undefined;
}